import React, { useMemo } from 'react';
import { Popper, Paper, Box, Typography, Fade, Chip } from '@mui/material';
import { TOOLBAR_BG_DARK } from '../../utils/colors';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const LANGUAGE_MAP = {
  'Python':        'python',
  'Python3':       'python',
  'C++':           'cpp',
  'C':             'c',
  'Java':          'java',
  'C#':            'csharp',
  'JavaScript':    'javascript',
  'TypeScript':    'typescript',
  'Go':            'go',
  'Rust':          'rust',
  'Swift':         'swift',
  'Kotlin':        'kotlin',
  'Ruby':          'ruby',
  'Scala':         'scala',
  'PHP':           'php',
  'MySQL':         'sql',
  'MS SQL Server': 'sql',
  'Oracle':        'sql',
};

const getPrismLanguage = (language) => LANGUAGE_MAP[language] ?? 'plaintext';

const PREVIEW_LINES = 10;
const TOOLTIP_WIDTH = 360;

const CodeTooltip = ({
  anchorEl,
  open,
  code = '',
  language = '',
  problemTitle = '',
  studentName = '',
}) => {
  const previewCode = useMemo(() => {
    const lines = code.split('\n');
    const preview = lines.slice(0, PREVIEW_LINES).join('\n');
    return lines.length > PREVIEW_LINES ? `${preview}\n// ...` : preview;
  }, [code]);

  // Custom modifier to force the popper to the left and keep it in-viewport
  const modifiers = useMemo(() => [
    {
      name: 'offset',
      options: { offset: [0, 12] },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['right', 'bottom', 'top'],
      },
    },
    {
      name: 'preventOverflow',
      options: {
        boundary: 'viewport',
        padding: 12,
      },
    },
  ], []);

  return (
    <Popper
      open={open && !!anchorEl}
      anchorEl={anchorEl}
      placement="left"
      transition
      modifiers={modifiers}
      style={{ zIndex: 1400, maxWidth: TOOLTIP_WIDTH }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={150}>
          <Paper
            elevation={8}
            sx={{
              width: TOOLTIP_WIDTH,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              pointerEvents: 'none', // tooltip only — clicks pass through to cell
            }}
          >
            {/* Header */}
            <Box
              sx={{
                px: 1.5,
                py: 1,
                background: TOOLBAR_BG_DARK,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="#e0e0e0"
                  display="block"
                  noWrap
                  sx={{ maxWidth: 220 }}
                >
                  {problemTitle || 'Problem'}
                </Typography>
                <Typography variant="caption" color="#9e9e9e" noWrap>
                  {studentName}
                </Typography>
              </Box>
              {language && (
                <Chip
                  label={language}
                  size="small"
                  variant="outlined"
                  sx={{ color: '#bdbdbd', borderColor: '#555', fontSize: '0.65rem' }}
                />
              )}
            </Box>

            {/* Code preview */}
            <Box sx={{ maxHeight: 240, overflowY: 'hidden' }}>
              <SyntaxHighlighter
                language={getPrismLanguage(language)}
                style={atomDark}
                showLineNumbers
                wrapLongLines
                customStyle={{
                  margin: 0,
                  borderRadius: 0,
                  fontSize: '0.72rem',
                  lineHeight: 1.5,
                  maxHeight: 240,
                  overflowY: 'hidden',
                  background: '#1a1a2e',
                }}
                lineNumberStyle={{ color: '#555', minWidth: '2em' }}
              >
                {previewCode || '// No code available.'}
              </SyntaxHighlighter>
            </Box>

            {/* Footer hint */}
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                background: '#12121f',
                borderTop: '1px solid #2a2a3f',
              }}
            >
              <Typography variant="caption" color="#757575">
                Click to view full solution
              </Typography>
            </Box>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};

export default CodeTooltip;
